document.addEventListener('DOMContentLoaded', () => {
  const revealElements = document.querySelectorAll('[data-reveal]');

  if ('IntersectionObserver' in window && revealElements.length > 0) {
    const revealObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -40px 0px'
      }
    );

    revealElements.forEach((element) => revealObserver.observe(element));
  } else {
    revealElements.forEach((element) => element.classList.add('is-visible'));
  }

  const siteHeader = document.querySelector('[data-site-header]');

  if (siteHeader) {
    let scrollStopTimer = null;
    const showHeaderBrand = () => {
      siteHeader.classList.remove('is-scrolling');
    };

    const syncHeaderScrollState = () => {
      siteHeader.classList.add('is-scrolling');

      if (scrollStopTimer !== null) {
        window.clearTimeout(scrollStopTimer);
      }

      scrollStopTimer = window.setTimeout(() => {
        showHeaderBrand();
      }, 180);
    };

    window.addEventListener('scroll', syncHeaderScrollState, { passive: true });

    if ('onscrollend' in window) {
      window.addEventListener('scrollend', showHeaderBrand, { passive: true });
    }

    showHeaderBrand();
  }

  const cartRoot = window.Shopify?.routes?.root || '/';
  const cartToggle = document.querySelector('[data-cart-toggle]');
  const cartDrawer = document.querySelector('[data-cart-drawer]');
  const cartItems = cartDrawer?.querySelector('[data-cart-items]');
  const cartEmpty = cartDrawer?.querySelector('[data-cart-empty]');
  const cartFooter = cartDrawer?.querySelector('[data-cart-footer]');
  const cartSubtotal = cartDrawer?.querySelector('[data-cart-subtotal]');
  const cartCountNodes = document.querySelectorAll('[data-cart-count]');
  const cartCloseButtons = document.querySelectorAll('[data-cart-drawer-close]');
  const cartCurrencyCode = cartDrawer?.dataset.currencyCode || 'USD';
  const cartRemoveLabel = cartDrawer?.dataset.cartRemoveLabel || 'Remove';
  const cartDecreaseLabel = cartDrawer?.dataset.cartDecreaseLabel || 'Decrease quantity for [product]';
  const cartQuantityLabel = cartDrawer?.dataset.cartQuantityLabel || 'Quantity for [product]';
  const cartIncreaseLabel = cartDrawer?.dataset.cartIncreaseLabel || 'Increase quantity for [product]';

  const escapeHtml = (value = '') =>
    String(value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');

  const formatCurrency = (cents) => {
    const amount = (Number(cents) || 0) / 100;

    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: cartCurrencyCode
      }).format(amount);
    } catch (_error) {
      return amount.toFixed(2);
    }
  };

  const formatCartLabel = (template, productTitle) =>
    String(template || '')
      .replaceAll('[product]', productTitle || '')
      .replaceAll('{product}', productTitle || '');

  const updateCartCount = (itemCount) => {
    cartCountNodes.forEach((node) => {
      node.textContent = String(itemCount);
      node.hidden = itemCount < 1;
    });
  };

  const buildCartItemOptions = (item) => {
    if (Array.isArray(item.options_with_values) && item.options_with_values.length > 0) {
      return `
        <dl class="ns-cart-drawer__item-options">
          ${item.options_with_values
            .map(
              (option) => `
                <div class="ns-cart-drawer__item-option">
                  <dt>${escapeHtml(option.name)}</dt>
                  <dd>${escapeHtml(option.value)}</dd>
                </div>
              `
            )
            .join('')}
        </dl>
      `;
    }

    if (item.variant_title && item.variant_title !== 'Default Title') {
      return `<p class="ns-cart-drawer__item-variant">${escapeHtml(item.variant_title)}</p>`;
    }

    return '';
  };

  const buildCartItemQuantityControls = (item) => `
    <div class="ns-quantity ns-cart-drawer__quantity">
      <button
        type="button"
        class="ns-quantity__button"
        data-cart-line-decrement
        data-cart-line-key="${escapeHtml(item.key)}"
        aria-label="${escapeHtml(formatCartLabel(cartDecreaseLabel, item.product_title))}"
      >
        -
      </button>
      <input
        class="ns-cart-drawer__quantity-input"
        type="number"
        value="${item.quantity}"
        readonly
        aria-label="${escapeHtml(formatCartLabel(cartQuantityLabel, item.product_title))}"
      >
      <button
        type="button"
        class="ns-quantity__button"
        data-cart-line-increment
        data-cart-line-key="${escapeHtml(item.key)}"
        aria-label="${escapeHtml(formatCartLabel(cartIncreaseLabel, item.product_title))}"
      >
        +
      </button>
    </div>
  `;

  const renderCartDrawer = (cart) => {
    if (!cartDrawer || !cartItems || !cartEmpty || !cartFooter || !cartSubtotal) {
      return;
    }

    updateCartCount(cart.item_count || 0);
    cartSubtotal.textContent = formatCurrency(cart.total_price);

    if (!Array.isArray(cart.items) || cart.items.length === 0) {
      cartItems.innerHTML = '';
      cartEmpty.hidden = false;
      cartFooter.hidden = true;
      return;
    }

    cartEmpty.hidden = true;
    cartFooter.hidden = false;
    cartItems.innerHTML = cart.items
      .map((item) => {
        const itemImage = item.image
          ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" class="ns-cart-drawer__item-image">`
          : `<div class="ns-cart-drawer__item-image ns-cart-drawer__item-image--placeholder" aria-hidden="true"></div>`;

        return `
          <article class="ns-cart-drawer__item" data-cart-line="${escapeHtml(item.key)}" data-cart-current-quantity="${item.quantity}">
            <a href="${escapeHtml(item.url || cartRoot)}" class="ns-cart-drawer__item-media">
              ${itemImage}
            </a>

            <div class="ns-cart-drawer__item-content">
              <a href="${escapeHtml(item.url || cartRoot)}" class="ns-cart-drawer__item-title">
                ${escapeHtml(item.product_title)}
              </a>
              ${buildCartItemOptions(item)}
              <div class="ns-cart-drawer__item-row">
                ${buildCartItemQuantityControls(item)}
                <strong>${formatCurrency(item.final_line_price)}</strong>
              </div>
              <button
                type="button"
                class="ns-cart-drawer__remove"
                data-cart-line-remove
                data-cart-line-key="${escapeHtml(item.key)}"
              >
                ${escapeHtml(cartRemoveLabel)}
              </button>
            </div>
          </article>
        `;
      })
      .join('');
  };

  const openCartDrawer = () => {
    if (!cartDrawer) {
      return;
    }

    cartDrawer.hidden = false;
    window.requestAnimationFrame(() => {
      cartDrawer.classList.add('is-open');
    });

    document.body.classList.add('ns-cart-drawer-open');
    cartToggle?.setAttribute('aria-expanded', 'true');
  };

  const closeCartDrawer = () => {
    if (!cartDrawer) {
      return;
    }

    cartDrawer.classList.remove('is-open');
    document.body.classList.remove('ns-cart-drawer-open');
    cartToggle?.setAttribute('aria-expanded', 'false');

    window.setTimeout(() => {
      if (!cartDrawer.classList.contains('is-open')) {
        cartDrawer.hidden = true;
      }
    }, 280);
  };

  const animateCartToggle = () => {
    if (!cartToggle) {
      return;
    }

    cartToggle.classList.remove('is-updated');
    void cartToggle.offsetWidth;
    cartToggle.classList.add('is-updated');

    window.setTimeout(() => {
      cartToggle.classList.remove('is-updated');
    }, 520);
  };

  const openModal = (modal) => {
    if (!modal) {
      return;
    }

    modal.hidden = false;
    document.body.classList.add('ns-modal-open');

    window.requestAnimationFrame(() => {
      modal.classList.add('is-open');
    });
  };

  const closeModal = (modal) => {
    if (!modal) {
      return;
    }

    modal.classList.remove('is-open');
    document.body.classList.remove('ns-modal-open');

    window.setTimeout(() => {
      if (!modal.classList.contains('is-open')) {
        modal.hidden = true;
      }
    }, 220);
  };

  const refreshCartDrawer = async ({ open = false, animate = false } = {}) => {
    if (!cartDrawer) {
      return null;
    }

    const response = await fetch(`${cartRoot}cart.js`, {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to load cart data.');
    }

    const cart = await response.json();
    renderCartDrawer(cart);

    if (animate) {
      animateCartToggle();
    }

    if (open) {
      openCartDrawer();
    }

    return cart;
  };

  if (cartToggle && cartDrawer) {
    cartToggle.addEventListener('click', async (event) => {
      event.preventDefault();

      if (cartDrawer.classList.contains('is-open')) {
        closeCartDrawer();
        return;
      }

      try {
        await refreshCartDrawer({ open: true });
      } catch (error) {
        window.location.href = cartToggle.getAttribute('href') || `${cartRoot}cart`;
      }
    });
  }

  cartCloseButtons.forEach((button) => {
    button.addEventListener('click', closeCartDrawer);
  });

  document.querySelectorAll('[data-modal-open]').forEach((button) => {
    button.addEventListener('click', () => {
      const modal = document.getElementById(button.dataset.modalOpen);
      openModal(modal);
    });
  });

  document.querySelectorAll('[data-modal-close]').forEach((button) => {
    button.addEventListener('click', () => {
      closeModal(button.closest('[data-modal]'));
    });
  });

  cartItems?.addEventListener('click', async (event) => {
    const control = event.target.closest('[data-cart-line-decrement], [data-cart-line-increment], [data-cart-line-remove]');

    if (!control) {
      return;
    }

    const lineKey = control.dataset.cartLineKey;
    const lineRoot = control.closest('[data-cart-line]');
    const quantityNode = lineRoot?.querySelector('.ns-cart-drawer__quantity-input');
    const currentQuantity = Number(lineRoot?.dataset.cartCurrentQuantity) || Number(quantityNode?.value) || 0;
    let nextQuantity = currentQuantity;

    if (control.hasAttribute('data-cart-line-remove')) {
      nextQuantity = 0;
    } else if (control.hasAttribute('data-cart-line-increment')) {
      nextQuantity = currentQuantity + 1;
    } else if (control.hasAttribute('data-cart-line-decrement')) {
      nextQuantity = Math.max(0, currentQuantity - 1);
    }

    if (!lineKey || (!control.hasAttribute('data-cart-line-remove') && nextQuantity === currentQuantity)) {
      return;
    }

    try {
      const response = await fetch(`${cartRoot}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: nextQuantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update the cart.');
      }

      const cart = await response.json();
      renderCartDrawer(cart);
      animateCartToggle();
      openCartDrawer();
    } catch (_error) {
      window.location.href = cartToggle?.getAttribute('href') || `${cartRoot}cart`;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') {
      return;
    }

    const openModalNode = document.querySelector('[data-modal].is-open');

    if (openModalNode) {
      closeModal(openModalNode);
      return;
    }

    if (cartDrawer?.classList.contains('is-open')) {
      closeCartDrawer();
    }
  });

  const setupRotatingGallery = (gallery, itemSelector) => {
    const galleryItems = Array.from(gallery.querySelectorAll(itemSelector));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const rotationDelay = Number(gallery.dataset.rotationDelay) || 4800;
    let activeIndex = galleryItems.findIndex((item) => item.classList.contains('is-active'));
    let rotationId = null;

    if (galleryItems.length < 2 || reduceMotion.matches) {
      return;
    }

    if (activeIndex < 0) {
      activeIndex = 0;
      galleryItems[0].classList.add('is-active');
    }

    const setActiveItem = (nextIndex) => {
      galleryItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === nextIndex);
      });
      activeIndex = nextIndex;
    };

    const stopRotation = () => {
      if (rotationId !== null) {
        window.clearInterval(rotationId);
        rotationId = null;
      }
    };

    const startRotation = () => {
      stopRotation();
      rotationId = window.setInterval(() => {
        const nextIndex = (activeIndex + 1) % galleryItems.length;
        setActiveItem(nextIndex);
      }, rotationDelay);
    };

    gallery.addEventListener('mouseenter', stopRotation);
    gallery.addEventListener('mouseleave', startRotation);
    gallery.addEventListener('focusin', stopRotation);
    gallery.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!gallery.contains(document.activeElement) && !document.hidden) {
          startRotation();
        }
      }, 0);
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stopRotation();
      } else {
        startRotation();
      }
    });

    startRotation();
  };

  document.querySelectorAll('[data-featured-gallery]').forEach((gallery) => {
    setupRotatingGallery(gallery, '[data-featured-gallery-item]');
  });

  document.querySelectorAll('[data-product-card-gallery]').forEach((gallery) => {
    setupRotatingGallery(gallery, '[data-product-card-gallery-item]');
  });

  document.querySelectorAll('[data-product-root]').forEach((productRoot) => {
    const productForm = productRoot.querySelector('.ns-product__form');
    const variantSelect = productRoot.querySelector('[data-variant-select]');
    const priceNode = productRoot.querySelector('[data-product-price]');
    const compareNode = productRoot.querySelector('[data-product-compare-price]');
    const availabilityNode = productRoot.querySelector('[data-product-availability]');
    const submitButton = productRoot.querySelector('.ns-product__submit');
    const featuredItems = Array.from(productRoot.querySelectorAll('.ns-product__featured-item[data-media-id]'));
    const mediaPrevButton = productRoot.querySelector('[data-media-prev]');
    const mediaNextButton = productRoot.querySelector('[data-media-next]');
    const optionGroups = Array.from(productRoot.querySelectorAll('[data-option-group]'));
    const optionButtons = productRoot.querySelectorAll('[data-option-button]');
    const quantityInput = productRoot.querySelector('[data-quantity-input]');
    const quantityIncrement = productRoot.querySelector('[data-quantity-increment]');
    const quantityDecrement = productRoot.querySelector('[data-quantity-decrement]');
    const buyNowButton = productRoot.querySelector('[data-buy-now]');
    const submitButtonText = submitButton ? submitButton.textContent.trim() : '';
    const buyNowButtonText = buyNowButton ? buyNowButton.textContent.trim() : '';
    const getVariantOptionValue = (optionNode, position) =>
      optionNode?.getAttribute(`data-option-${position}`) || optionNode?.dataset?.[`option${position}`] || '';

    const getActiveMediaIndex = () => featuredItems.findIndex((item) => item.classList.contains('is-active'));

    const setActiveMedia = (mediaId) => {
      if (!mediaId) {
        return;
      }

      featuredItems.forEach((item) => {
        item.classList.toggle('is-active', item.dataset.mediaId === mediaId);
      });
    };

    const stepActiveMedia = (direction) => {
      if (featuredItems.length < 2) {
        return;
      }

      const activeIndex = getActiveMediaIndex();
      const startIndex = activeIndex < 0 ? 0 : activeIndex;
      const nextIndex = (startIndex + direction + featuredItems.length) % featuredItems.length;
      const nextMediaId = featuredItems[nextIndex]?.dataset.mediaId;

      if (nextMediaId) {
        setActiveMedia(nextMediaId);
      }
    };

    mediaPrevButton?.addEventListener('click', () => {
      stepActiveMedia(-1);
    });

    mediaNextButton?.addEventListener('click', () => {
      stepActiveMedia(1);
    });

    const syncOptionButtonsFromVariant = (selectedOption) => {
      if (!selectedOption || optionGroups.length === 0) {
        return;
      }

      optionGroups.forEach((group, index) => {
        const optionValue = getVariantOptionValue(selectedOption, index + 1);

        group.querySelectorAll('[data-option-button]').forEach((button) => {
          const isSelected = button.dataset.optionValue === optionValue;
          button.classList.toggle('is-selected', isSelected);
          button.setAttribute('aria-pressed', String(isSelected));
        });
      });
    };

    const getSelectedColorMediaId = () => {
      const selectedColorButton = productRoot.querySelector(
        '[data-option-group][data-option-style="color"] [data-option-button].is-selected'
      );

      return selectedColorButton?.dataset.optionMediaId || '';
    };

    const findMatchingVariantOption = () => {
      if (!variantSelect || optionGroups.length === 0) {
        return null;
      }

      const selectedValues = optionGroups.map((group) => {
        const selectedButton = group.querySelector('[data-option-button].is-selected');
        return selectedButton ? selectedButton.dataset.optionValue : '';
      });

      return Array.from(variantSelect.options).find((option) =>
        selectedValues.every((value, index) => getVariantOptionValue(option, index + 1) === value)
      );
    };

    const clampQuantity = () => {
      if (!quantityInput) {
        return;
      }

      const min = Number(quantityInput.min) || 1;
      const max = quantityInput.max ? Number(quantityInput.max) : Number.POSITIVE_INFINITY;
      const rawValue = Number(quantityInput.value) || min;
      const nextValue = Math.min(max, Math.max(min, rawValue));

      quantityInput.value = String(nextValue);
    };

    if (variantSelect) {
      const syncVariantState = () => {
        const selectedOption = variantSelect.selectedOptions[0];

        if (!selectedOption) {
          return;
        }

        if (priceNode && selectedOption.dataset.price) {
          priceNode.textContent = selectedOption.dataset.price;
        }

        if (compareNode) {
          const comparePrice = selectedOption.dataset.compareAt || '';
          compareNode.textContent = comparePrice;
          compareNode.hidden = comparePrice === '';
        }

        if (availabilityNode) {
          const available = selectedOption.dataset.available === 'true';
          availabilityNode.textContent = available
            ? availabilityNode.dataset.inStockText
            : availabilityNode.dataset.soldOutText;
          availabilityNode.classList.toggle('is-sold-out', !available);

          if (submitButton) {
            submitButton.disabled = !available;
          }

          if (buyNowButton) {
            buyNowButton.disabled = !available;
          }
        }

        syncOptionButtonsFromVariant(selectedOption);
        setActiveMedia(selectedOption.dataset.featuredMediaId || getSelectedColorMediaId());
      };

      optionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const optionGroup = button.closest('[data-option-group]');
          const currentSelectedOption = variantSelect.selectedOptions[0];
          const optionStyle = optionGroup ? optionGroup.dataset.optionStyle || 'default' : 'default';

          if (!optionGroup || !currentSelectedOption) {
            return;
          }

          optionGroup.querySelectorAll('[data-option-button]').forEach((groupButton) => {
            const isSelected = groupButton === button;
            groupButton.classList.toggle('is-selected', isSelected);
            groupButton.setAttribute('aria-pressed', String(isSelected));
          });

          const matchingVariantOption = findMatchingVariantOption();

          if (!matchingVariantOption) {
            syncOptionButtonsFromVariant(currentSelectedOption);
            return;
          }

          variantSelect.value = matchingVariantOption.value;
          syncVariantState();

          if (optionStyle === 'color' && button.dataset.optionMediaId) {
            setActiveMedia(button.dataset.optionMediaId);
          }
        });
      });

      variantSelect.addEventListener('change', syncVariantState);
      syncVariantState();
    } else if (availabilityNode && submitButton) {
      const available = productRoot.dataset.defaultAvailable === 'true';
      availabilityNode.textContent = available
        ? availabilityNode.dataset.inStockText
        : availabilityNode.dataset.soldOutText;
      availabilityNode.classList.toggle('is-sold-out', !available);
      submitButton.disabled = !available;
      if (buyNowButton) {
        buyNowButton.disabled = !available;
      }
    }

    if (quantityInput) {
      quantityIncrement?.addEventListener('click', () => {
        quantityInput.value = String((Number(quantityInput.value) || 1) + 1);
        clampQuantity();
      });

      quantityDecrement?.addEventListener('click', () => {
        quantityInput.value = String((Number(quantityInput.value) || 1) - 1);
        clampQuantity();
      });

      quantityInput.addEventListener('change', clampQuantity);
      quantityInput.addEventListener('blur', clampQuantity);
      clampQuantity();
    }

    if (productForm && submitButton) {
      productForm.addEventListener('submit', async (event) => {
        event.preventDefault();

        const isBuyNow = event.submitter?.hasAttribute('data-buy-now') || false;
        const activeButton = isBuyNow && buyNowButton ? buyNowButton : submitButton;

        submitButton.disabled = true;
        if (buyNowButton) {
          buyNowButton.disabled = true;
        }

        if (activeButton) {
          activeButton.textContent = isBuyNow ? 'Processing...' : 'Adding...';
        }

        try {
          const response = await fetch(`${cartRoot}cart/add.js`, {
            method: 'POST',
            headers: {
              Accept: 'application/json'
            },
            body: new FormData(productForm)
          });

          if (!response.ok) {
            throw new Error('Unable to add this item right now.');
          }

          if (isBuyNow) {
            window.location.href = `${cartRoot}checkout`;
            return;
          }

          await refreshCartDrawer({ open: true, animate: true });
        } catch (error) {
          productForm.submit();
          return;
        } finally {
          submitButton.textContent = submitButtonText;
          if (buyNowButton) {
            buyNowButton.textContent = buyNowButtonText;
          }

          if (variantSelect) {
            const selectedOption = variantSelect.selectedOptions[0];
            const isUnavailable = selectedOption ? selectedOption.dataset.available !== 'true' : false;
            submitButton.disabled = isUnavailable;
            if (buyNowButton) {
              buyNowButton.disabled = isUnavailable;
            }
          } else if (availabilityNode) {
            const isUnavailable = availabilityNode.classList.contains('is-sold-out');
            submitButton.disabled = isUnavailable;
            if (buyNowButton) {
              buyNowButton.disabled = isUnavailable;
            }
          } else {
            submitButton.disabled = false;
            if (buyNowButton) {
              buyNowButton.disabled = false;
            }
          }
        }
      });
    }
  });
});
